import { useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { Document4Icon, House2Icon, LockIcon, Star2Icon } from "@/shared/icons";
import { Button } from "@/shared/ui";
import DeviceTabContentWrapper from "@/shared/widgets/DeviceTabContentWrapper";
import { estateSelector, setIsLocked } from "@/domains/estate/slice";
import useToggleLockEstate from "@/domains/estate/hooks/useToggleLockEstate";
import EstateSellContainer from "@/domains/estate/containers/tablet/EstateSellContainer";
import EstateSetResidentContainer from "@/domains/estate/containers/tablet/EstateSetResidentContainer";
import EstateMainInfo from "@/domains/estate/components/EstateMainInfo";
import EstateView from "@/domains/estate/components/EstateView";

type Props = {
  onClose: () => void;
  isFirstRender: boolean;
};

export default function HomeMenuInfoContainer({ onClose, isFirstRender }: Props) {
  const { t } = useTranslation("estate");
  const estate = useAppSelector(estateSelector);
  const [isSellShow, setIsSellShow] = useState(false);
  const [isResidenceShow, setIsResidenceShow] = useState(false);
  const dispatch = useAppDispatch();

  const { toggleLockEstate } = useToggleLockEstate();

  if (!estate) {
    return null;
  }

  const header = (
    <div className="flex items-center gap-2.5 w-full">
      <div className="text-18 text-white font-bold">
        {t(`estate.type.${estate.type}.name`)} #{estate.id}
      </div>
      <div className=" bg-white flex items-center gap-1 py-[0.3125rem] pl-[0.3125rem] pr-[0.4375rem] rounded-[0.3125rem] uppercase">
        <Star2Icon size={12} className="text-dark-800" />
        <div className="text-12 text-[#0F0F0F] font-bold leading-0">
          {t(`estate.level.${estate.level}`)}
        </div>
      </div>
    </div>
  );

  const footer = (
    <div className="flex gap-2.5 mt-5">
      <Button
        data-mz-accent="success"
        color="accent"
        className="flex grow items-center gap-2.5 h-11 px-4 py-3.5 font-bold"
        onClick={() => setIsSellShow(true)}
      >
        <House2Icon size={16} />
        {t("estate.sell")}
      </Button>
      <Button
        color="gray"
        variant="soft"
        className="h-11 flex items-center gap-2.5 px-4 py-3.5 font-bold"
        onClick={() => setIsResidenceShow(true)}
      >
        <Document4Icon size={16} />
        {t("estate.registration")}
      </Button>
      <Button
        color="gray"
        variant="soft"
        className="h-11 flex items-center gap-2.5 min-w-[9.375rem] px-4 py-3.5 font-bold"
        onClick={() => toggleLockEstate(estate, () => dispatch(setIsLocked(!estate.isLocked)))}
      >
        <LockIcon size={16} />
        {estate?.isLocked ? t("estate.lock") : t("estate.unlock")}
      </Button>
    </div>
  );

  return (
    <>
      {isSellShow && (
        <EstateSellContainer
          setIsShow={setIsSellShow}
          onSuccessSell={() => {
            onClose();
          }}
        />
      )}
      {isResidenceShow && <EstateSetResidentContainer setIsShow={setIsResidenceShow} />}
      <DeviceTabContentWrapper
        header={header}
        onClose={() => onClose()}
        footer={footer}
        className="p-0"
        isStaticFirst={isFirstRender}
      >
        <div className="flex flex-col items-stretch gap-5 w-full">
          <EstateView estate={estate} isResidence={false} />
          <EstateMainInfo info={estate} hideDetails />
        </div>
      </DeviceTabContentWrapper>
    </>
  );
}
